import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
// import logo from "../../assets/Klyropay 4.svg";
import logo from "../../assets/Klyropay.png"
import { register } from "../../services/authService";
import { handleApiError } from "../../utils/handleApiError";

const schema = Yup.object({
  email: Yup.string()
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Enter a valid email address")
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^(?:\+91[\-\s]?)?[6-9]\d{9}$/, "Enter a valid Indian mobile number")
    .required("Phone number is required"),
  password: Yup.string()
    .min(8, "Minimum 8 characters required")
    .matches(/[A-Z]/, "At least one uppercase letter required")
    .matches(/[a-z]/, "At least one lowercase letter required")
    .matches(/[0-9]/, "At least one number required")
    .matches(/[@$!%*?&]/, "At least one special character required")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords do not match")
    .required("Please confirm your password"),
  terms_accepted: Yup.boolean()
    .oneOf([true], "Please accept Terms & Conditions")
    .required("Please accept Terms & Conditions"),
});

function EyeIcon({ open }) {
  return open ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.97 9.97 0 012.563-4.568M6.228 6.228A9.97 9.97 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.97 9.97 0 01-4.423 5.568M6.228 6.228L3 3m3.228 3.228l3.65 3.65M17.772 17.772L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await schema.validate({ email, phone, password, confirmPassword, terms_accepted: agreed }, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const errs = {};
      err.inner.forEach((e) => { errs[e.path] = e.message; });
      setErrors(errs);
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await register({ email, mobile: phone, password, password_confirmation: confirmPassword, terms_accepted: agreed });
      const resData = res.data;
      const user_id = resData?.user_id || resData?.data?.user_id || resData?.id || resData?.data?.id || "";
      toast.success("Account created! Please verify your OTP.");
      navigate("/verify-code", { state: { email, mobile: phone, user_id } });
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Desktop input — same as login desktop
  const deskInputBase = "w-full pl-4 pr-4 py-3 rounded-[8px] bg-[#F3F4F5] text-[16px] text-gray-900 outline-none transition-all duration-200 placeholder:text-gray-400 focus:bg-white focus:shadow-[0_0_0_3px_rgba(124,58,237,0.08)]";
  const deskInputClass = (f) => `${deskInputBase} ${errors[f] ? "border-red-400 focus:border-red-400" : "border-gray-200 focus:border-violet-500"}`;

  // Mobile input — py-4
  const mobInputBase = "w-full pl-4 pr-4 py-3 rounded-[8px] bg-[#F3F4F5] text-[16px] text-gray-900 outline-none transition-all duration-200 placeholder:text-gray-400 focus:bg-white focus:shadow-[0_0_0_3px_rgba(124,58,237,0.08)] border-gray-200 focus:border-violet-500 py-6 text-[26px]";
  const mobInputClass = (f) => `${mobInputBase} ${errors[f] ? "border-red-400 focus:border-red-400" : "border-gray-200 focus:border-violet-500"}`;

  // const StrengthBar = ({ isMobile }) => (
  //   password.length > 0 ? (
  //     <div className="flex gap-1 mt-2">
  //       {[1,2,3,4].map((i) => (
  //         <div key={i} className={`${isMobile ? "h-1.5" : "h-1"} flex-1 rounded-full transition-all duration-300`} style={{
  //           background: password.length >= i * 3
  //             ? password.length < 6 ? "#f87171" : password.length < 10 ? "#fbbf24" : "#10b981"
  //             : "#e5e7eb"
  //         }} />
  //       ))}
  //     </div>
  //   ) : null
  // );

  return (
    <div className="flex min-h-screen w-full font-sans">

      {/* Left Panel — desktop only */}
      <div className="hidden lg:flex lg:w-[38%] flex-col px-12 py-12 pb-4 relative overflow-hidden bg-gradient-to-br from-[#754F89] to-[#4B2364]">
        <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full bg-violet-400/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-[10%] -left-10 w-64 h-64 rounded-full bg-purple-400/15 blur-3xl pointer-events-none" />
        <div className="relative h-auto max-w-[245px] justify-start mb-10">
          <img src={logo} alt="Klyropay" className="w-[200px] h-auto block object-cover" />
        </div>
        <div className="relative z-10 mb-24">
          <div className="space-y-5 mb-12">
            <h1 className="text-white font-extrabold text-[48px] leading-[1.0] tracking-tight">
              Empower Your Business <br /> Growth.
            </h1>
            <p className="text-white/55 text-sm leading-relaxed max-w-[300px]">
              Join the premier digital payments network. Secure, scalable, and built for modern merchant ecosystems.
            </p>
          </div>
        </div>
        <div className="relative z-10 flex items-center mt-18 gap-5 bg-[#593270] border border-[#5A3371] rounded-[16px] px-6 py-4">
          <div className="w-12 h-12 rounded-[12px] bg-white/20 flex items-center justify-center text-white">
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="30" fill="#FFFFFF" viewBox="0 0 24 24" stroke="#a78bfa" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <div>
            <p className="text-white text-[16px] font-semibold">Institutional Grade Security</p>
            <p className="text-purple-100/70 text-[14px] mt-0.5">PCI-DSS compliant infrastructure protecting every transaction.</p>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:flex-1 lg:w-[60%] bg-[#F5F3F8] lg:bg-white flex flex-col lg:items-center lg:justify-center md:justify-center">

        {/* Mobile/Tablet layout */}
        <div className="lg:hidden flex flex-col w-full px-5 sm:px-10 md:px-16 py-10 md:justify-center">
          <div className="bg-white rounded-[28px] shadow-[0px_8px_40px_rgba(75,35,100,0.10)] px-6 sm:px-8 md:px-10 py-8 sm:py-10 flex-1 flex flex-col md:justify-center">

            <div className="flex flex-col items-center mb-6">
              <img src={logo} alt="Klyropay" className="h-18 sm:h-20 md:h-40 lg:h-28 w-auto object-contain mb-3" />
            </div>

            <h2 className="text-[#191C1D] font-extrabold text-[40px] sm:text-[40px] mb-1">Create Account</h2>
            <p className="text-[#7D747F] text-[24px] sm:text-[26px] mb-7">Start your journey with Klyropay today.</p>

            <form onSubmit={handleSubmit} noValidate className="space-y-5 flex-1 flex flex-col">
              <div>
                <label className="block text-[20px] font-bold tracking-widest text-[#4C444E] mb-2 uppercase">Email Address</label>
                <input type="email" inputMode="email" autoComplete="email" placeholder="john@company.com" value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: undefined })); }}
                  className={mobInputClass("email")} />
                {errors.email && <p className="text-[13px] text-red-500 mt-1.5">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-[20px] font-bold tracking-widest text-[#4C444E] mb-2 uppercase">Phone Number</label>
                <div className="flex gap-2">
                  <div className="flex items-center gap-1.5 px-3 bg-[#F3F4F5] rounded-xl text-[14px] text-[#7D747F99] font-medium shrink-0 select-none">
                    <span>+91</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} className="text-gray-400">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </div>
                 <input
  type="tel"
  inputMode="numeric"
  autoComplete="tel"
  placeholder="98765 43210"
  value={phone}
  maxLength={10}
  onChange={(e) => {
    // Allow only numbers
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);

    setPhone(value);

    setErrors((prev) => ({
      ...prev,
      phone: undefined,
    }));
  }}

  // Prevent alphabet & special characters
  onKeyDown={(e) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(e.key)) return;

    // Only numeric keys allowed
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  }}

  // Prevent invalid paste
  onPaste={(e) => {
    const pastedData = e.clipboardData.getData("text");

    if (!/^\d+$/.test(pastedData)) {
      e.preventDefault();
    }
  }}

  className={mobInputClass("phone")}
/>
                </div>
                {errors.phone && <p className="text-[13px] text-red-500 mt-1.5">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-[20px] font-bold tracking-widest text-[#4C444E] mb-2 uppercase">Password</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} autoComplete="new-password" placeholder="Min. 8 characters" value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: undefined })); }}
                    className={`${mobInputClass("password")} pr-12`} />
                  <button type="button" onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
                {/* <StrengthBar isMobile={true} /> */}
                {errors.password && <p className="text-[13px] text-red-500 mt-1.5">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-[20px] font-semibold tracking-widest text-[#4C444E] mb-2 uppercase">Confirm Password</label>
                <input type="password" autoComplete="new-password" placeholder="Re-enter password" value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setErrors((p) => ({ ...p, confirmPassword: undefined })); }}
                  className={mobInputClass("confirmPassword")} />
                {errors.confirmPassword && <p className="text-[13px] text-red-500 mt-1.5">{errors.confirmPassword}</p>}
              </div>

              <div>
                <label className="flex items-start gap-3 cursor-pointer group" onClick={() => { setAgreed(v => !v); setErrors(p => ({ ...p, terms_accepted: undefined })); }}>
                  <div className={`mt-0.5 w-8 h-8 rounded shrink-0 border-2 flex items-center justify-center transition-all ${agreed ? "bg-violet-600 border-violet-600" : errors.terms_accepted ? "border-red-400" : "border-gray-300 group-hover:border-violet-400"}`}>
                    {agreed && <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>}
                  </div>
                  <span className="text-[24px] text-gray-500 leading-relaxed">
                    I agree to the{" "}
                    <a href="#" className="text-[#34094D] font-semibold hover:underline" onClick={e => e.stopPropagation()}>Terms &amp; Conditions</a>
                  </span>
                </label>
                {errors.terms_accepted && <p className="text-[13px] text-red-500 mt-1">{errors.terms_accepted}</p>}
              </div>

              <div className="mt-auto pt-2">
                <button type="submit" disabled={isSubmitting}
                  className={`w-full py-5 rounded-2xl text-white text-[30px] font-bold tracking-wide transition-all duration-200 bg-[linear-gradient(97.63deg,_#EA8534_0%,_#A0111A_100%)] shadow-[0px_12px_24px_0px_#EA85344D] flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.99] cursor-pointer ${isSubmitting ? "opacity-70 cursor-not-allowed" : "hover:opacity-90 active:scale-[0.99] cursor-pointer"}`}>
                  {isSubmitting && <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeOpacity="0.3" /><path d="M12 2a10 10 0 0 1 10 10" /></svg>}
                  {isSubmitting ? "Creating account…" : "Create Account"}
                </button>
                <p className="text-center mt-5 text-[22px] text-gray-500">
                  Already have an account?{" "}
                  <Link to="/" className="text-[#34094D] font-bold hover:underline">Sign In</Link>
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Desktop layout — same as login desktop */}
        <div className="hidden lg:block w-full lg:max-w-[420px]">
          <div className="mb-8">
            <h2 className="text-[#34094D] font-extrabold tracking-tight text-[30px] mb-1">Create Partner Account</h2>
            <p className="text-[#4C444E] text-[16px]">Start your journey with Klyropay Pay today.</p>
          </div>
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <label className="block text-[12px] font-semibold tracking-wide text-[#4C444E] mb-1.5 uppercase">Email Address</label>
              <input type="email" inputMode="email" autoComplete="email" placeholder="john@company.com" value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: undefined })); }}
                className={deskInputClass("email")} />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-[12px] font-semibold tracking-wide text-[#4C444E] mb-1.5 uppercase">Phone Number</label>
              <div className="flex gap-2">
                <div className="flex items-center gap-1.5 px-3 bg-[#F3F4F5] rounded-xl text-sm text-[#7D747F99] font-medium shrink-0 select-none">
                  <span>+91</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} className="text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </div>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="98765 43210"
                  value={phone}
                  maxLength={10}

                  onChange={(e) => {
                    // Allow only numbers
                    const value = e.target.value.replace(/\D/g, "");

                    setPhone(value);

                    setErrors((prev) => ({
                      ...prev,
                      phone: undefined,
                    }));
                  }}

                  // Prevent alphabet & special characters
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Delete",
                      "ArrowLeft",
                      "ArrowRight",
                      "Tab",
                      "Home",
                      "End",
                    ];

                    if (allowedKeys.includes(e.key)) return;

                    // Only numeric keys allowed
                    if (!/^[0-9]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}

                  // Prevent invalid paste
                  onPaste={(e) => {
                    const pastedData = e.clipboardData.getData("text");

                    if (!/^\d+$/.test(pastedData)) {
                      e.preventDefault();
                    }
                  }}

                  className={deskInputClass("phone")}
                />
              </div>
              {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
            </div>
            <div>
              <label className="block text-[12px] font-semibold tracking-wide text-[#4C444E] mb-1.5 uppercase">Password</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} autoComplete="new-password" placeholder="Min. 8 characters" value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: undefined })); }}
                  className={`${deskInputClass("password")} pr-11`} />
                <button type="button" onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                  <EyeIcon open={showPassword} />
                </button>
              </div>
              {/* <StrengthBar isMobile={false} /> */}
              {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
            </div>
            <div>
              <label className="block text-[12px] font-semibold tracking-wide text-[#4C444E] mb-1.5 uppercase">Confirm Password</label>
              <input type="password" autoComplete="new-password" placeholder="Re-enter password" value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setErrors((p) => ({ ...p, confirmPassword: undefined })); }}
                className={deskInputClass("confirmPassword")} />
              {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
            </div>
            <div>
              <label className="flex items-start gap-3 cursor-pointer group" onClick={() => { setAgreed(v => !v); setErrors(p => ({ ...p, terms_accepted: undefined })); }}>
                <div className={`mt-0.5 w-4 h-4 rounded shrink-0 border-2 flex items-center justify-center transition-all ${agreed ? "bg-violet-600 border-violet-600" : errors.terms_accepted ? "border-red-400" : "border-gray-300 group-hover:border-violet-400"}`}>
                  {agreed && <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>}
                </div>
                <span className="text-xs text-gray-500 leading-relaxed">
                  I agree to the{" "}
                  <a href="#" className="text-[#34094D] font-semibold hover:underline" onClick={e => e.stopPropagation()}>Terms &amp; Conditions</a>
                </span>
              </label>
              {errors.terms_accepted && <p className="text-xs text-red-500 mt-1">{errors.terms_accepted}</p>}
            </div>
            <button type="submit" disabled={isSubmitting}
              className={`w-full py-3.5 rounded-xl text-white text-[18px] font-bold tracking-wide transition-all duration-200 bg-[linear-gradient(97.63deg,_#EA8534_0%,_#A0111A_100%)] shadow-lg flex items-center justify-center gap-2 ${isSubmitting ? "opacity-70 cursor-not-allowed" : "hover:opacity-90 active:scale-[0.99] cursor-pointer"}`}>
              {isSubmitting && <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeOpacity="0.3" /><path d="M12 2a10 10 0 0 1 10 10" /></svg>}
              {isSubmitting ? "Creating account…" : "Create Account"}
            </button>
          </form>
          <p className="text-center mt-6 text-sm text-gray-500">
            Already have an account?{" "}
            <Link to="/" className="text-[#34094D] font-bold hover:underline">Sign In</Link>
          </p>
        </div>

      </div>
    </div>
  );
}
